import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { useTenant } from '@/contexts/TenantContext';
import { usePermissions } from '@/contexts/PermissionContext';
import {
  createTenant,
  cloneTenant,
  seedTestData,
  CreateTenantData,
} from '@/services/tenantService';
import { getDefaultTenant } from '@/services/domainService';
import { cn } from '@/lib/utils';
import { Loader2, Building2, Copy } from 'lucide-react';

// The global/platform tenant (holds global templates) is not offered as a clone
// source per product decision 2026-06-18. Sourced from the documented default tenant.
const GLOBAL_TENANT_ID = getDefaultTenant().tenantId;

type SourceMode = 'blank' | 'clone';
type DataMode = 'blank' | 'testdata';

interface CreateTenantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided, the dialog opens in "clone" mode pre-set to this source tenant. */
  cloneFromTenantId?: string | null;
}

export function CreateTenantDialog({
  open,
  onOpenChange,
  cloneFromTenantId = null,
}: CreateTenantDialogProps) {
  const { toast } = useToast();
  const { addTenant, userTenants } = useTenant();
  const { hasPermission } = usePermissions();
  const isSuperadmin = hasPermission('system.override');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);
  const [sourceMode, setSourceMode] = useState<SourceMode>('blank');
  const [sourceTenantId, setSourceTenantId] = useState<string>('');
  const [dataMode, setDataMode] = useState<DataMode>('blank');
  const [seedCount, setSeedCount] = useState<number>(20);
  const [formData, setFormData] = useState<CreateTenantData>({
    company_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof CreateTenantData | 'source' | 'count', string>>
  >({});

  const busy = isSubmitting || isSeeding;

  // Tenants offered as clone sources: accessible (already archived-free for
  // superadmins) minus the global/platform tenant.
  const cloneSources = userTenants.filter((t) => t.tenant_id !== GLOBAL_TENANT_ID);

  // Sync mode/source whenever the dialog opens (or the pre-set source changes).
  useEffect(() => {
    if (!open) return;
    if (cloneFromTenantId) {
      setSourceMode('clone');
      setSourceTenantId(cloneFromTenantId);
    } else {
      setSourceMode('blank');
      setSourceTenantId('');
    }
    setDataMode('blank');
    setSeedCount(20);
  }, [open, cloneFromTenantId]);

  const resetForm = () => {
    setFormData({
      company_name: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
    });
    setErrors({});
    setSourceMode('blank');
    setSourceTenantId('');
    setDataMode('blank');
    setSeedCount(20);
  };

  const handleChange = (field: keyof CreateTenantData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CreateTenantData | 'source' | 'count', string>> = {};

    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    }
    if (!formData.contact_name.trim()) {
      newErrors.contact_name = 'Contact name is required';
    }
    if (!formData.contact_email.trim()) {
      newErrors.contact_email = 'Contact email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.contact_email)) {
        newErrors.contact_email = 'Invalid email format';
      }
    }
    if (sourceMode === 'clone' && !sourceTenantId) {
      newErrors.source = 'Select a tenant to clone from';
    }
    if (isSuperadmin && dataMode === 'testdata') {
      if (!Number.isInteger(seedCount) || seedCount < 1 || seedCount > 100) {
        newErrors.count = 'Enter a project count between 1 and 100';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const isClone = sourceMode === 'clone';
      const result = isClone
        ? await cloneTenant({ ...formData, clone_from_tenant_id: sourceTenantId })
        : await createTenant(formData);

      if (result.success && result.tenant) {
        addTenant(result.tenant);
        const newTenantId = result.tenant.tenant_id;
        const companyName = result.tenant.company_name;
        const sourceName =
          cloneSources.find((t) => t.tenant_id === sourceTenantId)?.company_name ??
          'the source tenant';
        const literalWarnings = result.warnings?.notification_literal_name ?? [];
        const wantData = isSuperadmin && dataMode === 'testdata';

        // Seed test data as a SEPARATE call so a slow/failed seed never invalidates
        // the already-created tenant (AC5 partial-failure path).
        let seedNote = '';
        if (wantData) {
          setIsSeeding(true);
          const seed = await seedTestData(newTenantId, seedCount);
          setIsSeeding(false);
          if (seed.success) {
            seedNote = ` with ${seed.projects_created ?? seedCount} test projects`;
          } else {
            toast({
              title: 'Tenant created — test data failed',
              description: `${companyName} was created, but seeding test data failed: ${seed.error}. The tenant is usable; you can seed it again later.`,
              variant: 'destructive',
              duration: 12000,
            });
            resetForm();
            onOpenChange(false);
            return;
          }
        }

        toast({
          title: '✓ Tenant Created Successfully',
          description: isClone
            ? `${companyName} was created as a copy of ${sourceName}${seedNote}.` +
              (literalWarnings.length > 0
                ? ` Note: some notification templates may still contain the source company name — review under Settings → Notifications.`
                : '')
            : `${companyName} is ready${seedNote}! You can now switch to it from the tenant dropdown.`,
          duration: literalWarnings.length > 0 ? 10000 : 6000,
        });

        resetForm();
        onOpenChange(false);
      } else {
        // Keep the dialog open with input preserved.
        toast({
          title: 'Error',
          description: result.error || 'Failed to create tenant',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error creating tenant:', error);
      toast({
        title: 'Error',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
      setIsSeeding(false);
    }
  };

  const handleCancel = () => {
    if (!busy) {
      resetForm();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center space-x-2">
            <Building2 className="h-5 w-5 text-primary" />
            <DialogTitle>Create New Tenant</DialogTitle>
          </div>
          <DialogDescription>
            {sourceMode === 'clone'
              ? 'Create a new tenant as a copy of an existing one. Pipelines, checklists, notification templates, theme, and dashboards are cloned — users and projects are not.'
              : 'Create a new tenant organization. This will set up a new isolated environment with default settings.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Source selector (superadmin only) */}
          {isSuperadmin && (
            <div className="space-y-2">
              <Label>Source</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={sourceMode === 'blank' ? 'default' : 'outline'}
                  onClick={() => {
                    setSourceMode('blank');
                    setErrors((prev) => ({ ...prev, source: undefined }));
                  }}
                  disabled={busy}
                >
                  Blank template
                </Button>
                <Button
                  type="button"
                  variant={sourceMode === 'clone' ? 'default' : 'outline'}
                  onClick={() => setSourceMode('clone')}
                  disabled={busy}
                >
                  Clone existing tenant
                </Button>
              </div>

              {sourceMode === 'clone' && (
                <div className="space-y-1 pt-1">
                  <Label htmlFor="clone_source">
                    Clone from <span className="text-red-500">*</span>
                  </Label>
                  <select
                    id="clone_source"
                    value={sourceTenantId}
                    onChange={(e) => {
                      setSourceTenantId(e.target.value);
                      if (errors.source) {
                        setErrors((prev) => ({ ...prev, source: undefined }));
                      }
                    }}
                    disabled={busy}
                    className={cn(
                      'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                      errors.source ? 'border-red-500' : ''
                    )}
                  >
                    <option value="" disabled>
                      Select a tenant…
                    </option>
                    {cloneSources.map((t) => (
                      <option key={t.tenant_id} value={t.tenant_id}>
                        {t.company_name}
                      </option>
                    ))}
                  </select>
                  {errors.source && (
                    <p className="text-sm text-red-500">{errors.source}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Data selector (superadmin only) */}
          {isSuperadmin && (
            <div className="space-y-2">
              <Label>Data</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={dataMode === 'blank' ? 'default' : 'outline'}
                  onClick={() => {
                    setDataMode('blank');
                    setErrors((prev) => ({ ...prev, count: undefined }));
                  }}
                  disabled={busy}
                >
                  Blank slate
                </Button>
                <Button
                  type="button"
                  variant={dataMode === 'testdata' ? 'default' : 'outline'}
                  onClick={() => setDataMode('testdata')}
                  disabled={busy}
                >
                  With test data
                </Button>
              </div>

              {dataMode === 'testdata' && (
                <div className="space-y-1 pt-1">
                  <Label htmlFor="seed_count">Number of test projects</Label>
                  <Input
                    id="seed_count"
                    type="number"
                    min={1}
                    max={100}
                    value={Number.isNaN(seedCount) ? '' : seedCount}
                    onChange={(e) => {
                      setSeedCount(parseInt(e.target.value, 10));
                      if (errors.count) setErrors((prev) => ({ ...prev, count: undefined }));
                    }}
                    disabled={busy}
                    className={errors.count ? 'border-red-500' : ''}
                  />
                  {errors.count ? (
                    <p className="text-sm text-red-500">{errors.count}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Adds 2 synthetic users + sample projects, checklists, and documents (1–100).
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="company_name">
              Company Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="company_name"
              value={formData.company_name}
              onChange={handleChange('company_name')}
              placeholder="Acme Solar Solutions"
              disabled={busy}
              className={errors.company_name ? 'border-red-500' : ''}
            />
            {errors.company_name && (
              <p className="text-sm text-red-500">{errors.company_name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact_name">
              Contact Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="contact_name"
              value={formData.contact_name}
              onChange={handleChange('contact_name')}
              placeholder="John Smith"
              disabled={busy}
              className={errors.contact_name ? 'border-red-500' : ''}
            />
            {errors.contact_name && (
              <p className="text-sm text-red-500">{errors.contact_name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact_email">
              Contact Email <span className="text-red-500">*</span>
            </Label>
            <Input
              id="contact_email"
              type="email"
              value={formData.contact_email}
              onChange={handleChange('contact_email')}
              placeholder="john@acmesolar.com"
              disabled={busy}
              className={errors.contact_email ? 'border-red-500' : ''}
            />
            {errors.contact_email && (
              <p className="text-sm text-red-500">{errors.contact_email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact_phone">Contact Phone</Label>
            <Input
              id="contact_phone"
              type="tel"
              value={formData.contact_phone}
              onChange={handleChange('contact_phone')}
              placeholder="(555) 123-4567"
              disabled={busy}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {isSeeding ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Seeding test data…
                </>
              ) : isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {sourceMode === 'clone' ? 'Cloning…' : 'Creating...'}
                </>
              ) : sourceMode === 'clone' ? (
                <>
                  <Copy className="mr-2 h-4 w-4" />
                  Clone Tenant
                </>
              ) : (
                'Create Tenant'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
